import React, {
  useContext, useState, useMemo,
} from 'react';
import { Dimensions } from 'react-native';
import {
  NativeBaseProvider,
  ScrollView,
  Button,
  HStack,
} from 'native-base';
import {
  Dialog, Portal,
} from 'react-native-paper';
import { useMutation, useQuery } from '@apollo/client';
import { Formik } from 'formik';
import {
  date, object, string, number,
} from 'yup';
import { ModalContext } from '../../context/ModalContext';
import { AuthContext } from '../../context/AuthContext';
import { CREATE_TRIP, UPDATE_TRIP } from '../../gql/trip/mutation';
import { GET_DRIVERS } from '../../gql/user/query';
import { GET_DRIVER_GROUPS } from '../../gql/driver-group/query';
import { GET_TRANSPORTATIONS } from '../../gql/transportation/query';
import { TripsForEvent } from '../../gql/trip/query';
import LoadingIndicator from '../../components/commons/LoadingIndicator';
import { FormStepOne, getMissingDriverDocuments } from './StepsFormManageTrip';

const validationSchema = object({
  transportationId: number().nullable().required('Transport requis'),
  driverId: number().nullable().required('Conducteur requis'),
  departureLocation: string().required('Lieu de départ requis'),
  arrivalLocation: string().required('Lieu d\'arrivée requis'),
  departureDateTime: date().required('Date et heure de départ requises'),
});

function ManageTrip() {
  const { displayManageTripModal, toggleManageTripModal } = useContext(ModalContext);
  const { authState } = useContext(AuthContext);
  const {
    title, trip, eventId, eventLocation, onSave,
  } = displayManageTripModal;
  const [openPicker, setOpenPicker] = useState(null);
  const [createTrip, { loading: creating }] = useMutation(CREATE_TRIP);
  const [updateTrip, { loading: updating }] = useMutation(UPDATE_TRIP);
  const isEditing = !!trip;
  const isLoading = creating || updating;

  const { data: driversData, loading: driversLoading } = useQuery(GET_DRIVERS);
  const {
    data: transportationsData,
    loading: transportationsLoading,
  } = useQuery(GET_TRANSPORTATIONS);
  const { data: driverGroupsData } = useQuery(GET_DRIVER_GROUPS);

  // Memoize initialValues before any early returns (hooks must be called unconditionally)
  const initialValues = useMemo(() => ({
    transportationId: trip?.transportation?.id || null,
    // Only default to the current admin when creating a new trip — when editing, an
    // existing trip with no driver (e.g. the driver's account was deleted) must stay
    // blank so it isn't silently reassigned to whoever opens the edit modal.
    driverId: isEditing ? (trip?.driver?.id || null) : (authState?.user?.id || null),
    departureLocation: trip?.departureLocation || '',
    arrivalLocation: trip?.arrivalLocation || eventLocation || '',
    departureDateTime: trip?.departureDateTime ? new Date(trip.departureDateTime) : new Date(),
    hasHotelStay: !!(trip?.hotelName || trip?.hotelLocation),
    hotelName: trip?.hotelName || '',
    hotelLocation: trip?.hotelLocation || '',
  }), [trip, authState?.user?.id, eventLocation]);

  if (driversLoading || transportationsLoading) {
    return <LoadingIndicator text="Chargement des données..." />;
  }

  const drivers = (driversData?.allUsers || []).filter(
    (user) => user.role === 'COACH' || user.role === 'SUPERADMIN' || user.status === 'COMPANION',
  );

  const transportations = transportationsData?.transportations || [];
  const driverGroups = driverGroupsData?.driverGroups || [];

  const handleSubmit = async (values) => {
    if (!eventId) {
      // eslint-disable-next-line no-alert
      alert('Error: eventId is missing from modal context');
      console.error('displayManageTripModal:', displayManageTripModal);
      return;
    }

    const variables = {
      input: {
        transportationId: values.transportationId,
        driverId: values.driverId,
        departureLocation: values.departureLocation,
        arrivalLocation: values.arrivalLocation,
        departureDateTime: values.departureDateTime,
        hotelName: values.hasHotelStay ? values.hotelName || null : null,
        hotelLocation: values.hasHotelStay ? values.hotelLocation || null : null,
        eventId: Number(eventId),
      },
    };

    try {
      if (isEditing) {
        await updateTrip({
          variables: { id: trip.id, ...variables },
          refetchQueries: [
            { query: TripsForEvent, variables: { eventId } },
          ],
        });
      } else {
        await createTrip({
          variables,
          refetchQueries: [
            { query: TripsForEvent, variables: { eventId } },
          ],
        });
      }

      if (onSave) {
        onSave();
      }
      toggleManageTripModal();
    } catch (error) {
      console.error('Error creating/updating trip:', error);
      // eslint-disable-next-line no-alert
      alert(`Error: ${error.message}`);
    }
  };

  // The fields scroll, the buttons stay pinned under them, so the form always fits the dialog.
  return (
    <Dialog
      visible={displayManageTripModal.display}
      dismissable
      onDismiss={toggleManageTripModal}
      style={{ maxHeight: 0.9 * Dimensions.get('window').height }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          handleSubmit: formikSubmit, values, errors, setFieldValue,
        }) => (
          <>
            <Dialog.ScrollArea>
              {/* Extra room while a dropdown is open, so its list is not clipped. */}
              <ScrollView
                contentContainerStyle={{
                  paddingHorizontal: 24,
                  paddingTop: 8,
                  paddingBottom: openPicker ? 260 : 8,
                }}
              >
                <FormStepOne
                  values={values}
                  errors={errors}
                  touched={{}}
                  setFieldValue={setFieldValue}
                  openPicker={openPicker}
                  setOpenPicker={setOpenPicker}
                  drivers={drivers}
                  transportations={transportations}
                  driverGroups={driverGroups}
                  estimatedDuration={trip?.estimatedDuration}
                />
              </ScrollView>
            </Dialog.ScrollArea>
            <Dialog.Actions>
              <HStack flex={1} justifyContent="space-between">
                <Button variant="outline" onPress={toggleManageTripModal}>Annuler</Button>
                <Button
                  onPress={formikSubmit}
                  isLoading={isLoading}
                  isDisabled={isLoading || (
                    values.driverId !== (trip?.driver?.id ?? null)
                    && getMissingDriverDocuments(
                      drivers.find((d) => d.id === values.driverId),
                    ).length > 0
                  )}
                >
                  Enregistrer
                </Button>
              </HStack>
            </Dialog.Actions>
          </>
        )}
      </Formik>
    </Dialog>
  );
}

export default function ManageTripModal() {
  const { displayManageTripModal } = useContext(ModalContext);
  if (!displayManageTripModal.display) {
    return null;
  }
  return (
    <Portal>
      <NativeBaseProvider>
        <ManageTrip />
      </NativeBaseProvider>
    </Portal>
  );
}
